const urlMaxLength = 2048;

function stripControlChars(value) {
  if (typeof value !== 'string') {
    return '';
  }
  let cleaned = '';
  for (let index = 0; index < value.length; index = index + 1) {
    const code = value.charCodeAt(index);
    if (code >= 32 && code !== 127) {
      cleaned = cleaned + value[index];
    }
  }
  return cleaned;
}

export function normalizeExternalBaseUrl(value) {
  const raw = typeof value === 'string' ? value : '';
  const cleaned = stripControlChars(raw).trim();
  if (cleaned === '') {
    return '';
  }
  if (cleaned.length > urlMaxLength) {
    return '';
  }
  let parsed = null;
  try {
    parsed = new URL(cleaned);
  } catch (error) {
    parsed = null;
  }
  if (parsed === null) {
    return '';
  }
  const protocol = typeof parsed.protocol === 'string' ? parsed.protocol.toLowerCase() : '';
  const isProd = process.env.environment === 'production';
  if (protocol !== 'https:' && (protocol !== 'http:' || isProd === true)) {
    return '';
  }
  parsed.pathname = '';
  parsed.search = '';
  parsed.hash = '';
  return parsed.toString().replace(/\/$/, '');
}

export function getCustomDomainBase(customDomains, serviceName) {
  const domains = typeof customDomains === 'object' && customDomains !== null ? customDomains : {};
  if (typeof domains[serviceName] !== 'string') {
    return '';
  }
  return normalizeExternalBaseUrl(domains[serviceName]);
}

export function buildExternalOpenUrl(base, routePath, agentId, appendRoutePath) {
  const normalizedBase = normalizeExternalBaseUrl(base);
  if (normalizedBase === '') {
    return null;
  }
  const raw = appendRoutePath === true ? `${normalizedBase}${routePath}/${agentId}` : `${normalizedBase}/${agentId}`;
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://localhost');
    const protocol = typeof parsed.protocol === 'string' ? parsed.protocol.toLowerCase() : '';
    const isProd = process.env.environment === 'production';
    if (protocol === 'https:' || (protocol === 'http:' && isProd === false)) {
      return parsed.href;
    }
  } catch (error) {}
  return null;
}

function resolvePublicAgentExternalBase(agent, customDomains) {
  const agentsDomain = getCustomDomainBase(customDomains, 'agents');
  if (agentsDomain !== '') {
    return agentsDomain;
  }
  if (agent.publicAgentServiceEndpoint !== undefined && agent.publicAgentServiceEndpoint !== null && String(agent.publicAgentServiceEndpoint).trim() !== '') {
    return normalizeExternalBaseUrl(String(agent.publicAgentServiceEndpoint).trim());
  }
  if (typeof window !== 'undefined' && window.env !== undefined && window.env !== null && typeof window.env.VUE_APP_PUBLIC_AGENT_SERVICE_ENDPOINT === 'string' && String(window.env.VUE_APP_PUBLIC_AGENT_SERVICE_ENDPOINT).trim() !== '') {
    return normalizeExternalBaseUrl(String(window.env.VUE_APP_PUBLIC_AGENT_SERVICE_ENDPOINT).trim());
  }
  return '';
}

function resolveVoiceAgentExternalBase(agent) {
  if (agent.voiceServiceUrl !== undefined && agent.voiceServiceUrl !== null && String(agent.voiceServiceUrl).trim() !== '') {
    return normalizeExternalBaseUrl(String(agent.voiceServiceUrl).trim());
  }
  if (agent.voiceAgentServiceEndpoint !== undefined && agent.voiceAgentServiceEndpoint !== null && String(agent.voiceAgentServiceEndpoint).trim() !== '') {
    return normalizeExternalBaseUrl(String(agent.voiceAgentServiceEndpoint).trim());
  }
  if (typeof window !== 'undefined' && window.env !== undefined && window.env !== null && typeof window.env.VUE_APP_WEB_VOICE_ENDPOINT === 'string' && String(window.env.VUE_APP_WEB_VOICE_ENDPOINT).trim() !== '') {
    return normalizeExternalBaseUrl(String(window.env.VUE_APP_WEB_VOICE_ENDPOINT).trim());
  }
  if (typeof process !== 'undefined' && process.env !== undefined && process.env !== null && typeof process.env.VUE_APP_WEB_VOICE_ENDPOINT === 'string' && String(process.env.VUE_APP_WEB_VOICE_ENDPOINT).trim() !== '') {
    return normalizeExternalBaseUrl(String(process.env.VUE_APP_WEB_VOICE_ENDPOINT).trim());
  }
  return '';
}

function resolveVoiceAgentGuid(agent) {
  if (agent.agentguid !== undefined && agent.agentguid !== null && String(agent.agentguid).trim() !== '') {
    return String(agent.agentguid).trim();
  }
  if (agent.agentGuid !== undefined && agent.agentGuid !== null && String(agent.agentGuid).trim() !== '') {
    return String(agent.agentGuid).trim();
  }
  if (agent.agentid !== undefined && agent.agentid !== null && String(agent.agentid).trim() !== '') {
    return String(agent.agentid).trim();
  }
  return '';
}

export function resolveOpenWebAgentTarget(agent) {
  if (agent === null || agent === undefined) {
    return null;
  }
  const agentId = agent.agentid;
  const agentType = agent.agenttype;
  const routePath = agentType === 'PublicAgent' ? '/agent' : '/aiChatAgentAssistance';
  const safeAgentId = encodeURIComponent(String(agentId));
  const customDomains = typeof agent.customDomains === 'object' && agent.customDomains !== null ? agent.customDomains : {};
  if (agentType === 'PublicAgent') {
    const voiceAgentId = resolveVoiceAgentGuid(agent);
    const voiceAgentExternal = resolveVoiceAgentExternalBase(agent);
    const externalVoiceUrl = voiceAgentId === '' ? null : buildExternalOpenUrl(voiceAgentExternal, '/agent', encodeURIComponent(voiceAgentId), true);
    if (externalVoiceUrl !== null) {
      return { mode: 'window', url: externalVoiceUrl };
    }
    const external = resolvePublicAgentExternalBase(agent, customDomains);
    const safe = buildExternalOpenUrl(external, routePath, safeAgentId, false);
    if (safe !== null) {
      return { mode: 'window', url: safe };
    }
    if (typeof window !== 'undefined') {
      return { mode: 'window', url: `${window.location.origin}${routePath}/${safeAgentId}` };
    }
    return null;
  }
  const assistantDomain = getCustomDomainBase(customDomains, 'assistant');
  const safe = buildExternalOpenUrl(assistantDomain, routePath, safeAgentId, true);
  if (safe !== null) {
    return { mode: 'window', url: safe };
  }
  return { mode: 'router', url: `${routePath}/${safeAgentId}` };
}

export function resolveOpenWebVoiceAgentTarget(agent) {
  if (agent === null || agent === undefined) {
    return null;
  }
  const agentId = resolveVoiceAgentGuid(agent);
  if (agentId === '') {
    return null;
  }
  const agentType = agent.agenttype;
  const routePath = '/webVoiceAgent';
  const safeAgentId = encodeURIComponent(String(agentId));
  const customDomains = typeof agent.customDomains === 'object' && agent.customDomains !== null ? agent.customDomains : {};
  const voiceAgentExternal = resolveVoiceAgentExternalBase(agent);
  const externalVoiceUrl = buildExternalOpenUrl(voiceAgentExternal, '/agent', safeAgentId, true);
  if (externalVoiceUrl !== null) {
    return { mode: 'window', url: externalVoiceUrl };
  }
  if (agentType === 'PublicAgent') {
    const external = resolvePublicAgentExternalBase(agent, customDomains);
    const safe = buildExternalOpenUrl(external, routePath, safeAgentId, true);
    if (safe !== null) {
      return { mode: 'window', url: safe };
    }
    if (typeof window !== 'undefined') {
      return { mode: 'window', url: `${window.location.origin}${routePath}/${safeAgentId}` };
    }
    return null;
  }
  const assistantDomain = getCustomDomainBase(customDomains, 'assistant');
  const safe = buildExternalOpenUrl(assistantDomain, routePath, safeAgentId, true);
  if (safe !== null) {
    return { mode: 'window', url: safe };
  }
  return { mode: 'router', url: `${routePath}/${safeAgentId}` };
}
